/**
 * Domain allowlist matcher for widget loader.
 *
 * Empty list = allow anywhere (opt-in security model).
 * Wildcards supported as `*.example.com` (matches subdomains, NOT the bare root).
 * Literal `localhost` matches any port; `localhost:3000` is exact-port.
 *
 * NEW-3 — IPv6 + port-aware. The host header may include a port (e.g.
 * `example.com:8443`) and an IPv6 address may be bracketed (`[::1]:3000`). We
 * normalise both sides via {@link splitHostPort}: an entry without a port matches
 * any port; an entry with an explicit port requires an exact match. Note that
 * the literal `localhost` entry does NOT cover the IPv6 loopback (`::1`) — users
 * who need that must add `::1` (or `[::1]`) to their allowlist explicitly.
 */

interface HostPort {
  host: string;
  port?: string;
}

function splitHostPort(input: string): HostPort {
  const s = (input || '').toLowerCase().trim();
  if (!s) return { host: '' };
  if (s.startsWith('[')) {
    const close = s.indexOf(']');
    if (close > 0) {
      const host = s.slice(1, close);
      const rest = s.slice(close + 1);
      const port = rest.startsWith(':') ? rest.slice(1) : undefined;
      return { host, port };
    }
  }
  const colon = s.lastIndexOf(':');
  // IPv6 unbracketed: contains multiple colons → not a host:port.
  if (colon > 0 && s.indexOf(':') === colon && /^\d+$/.test(s.slice(colon + 1))) {
    return { host: s.slice(0, colon), port: s.slice(colon + 1) };
  }
  return { host: s };
}

export function allowlistAllows(domains: string[], hostHeader: string): boolean {
  if (!domains || domains.length === 0) return true;
  const { host, port } = splitHostPort(hostHeader);
  if (!host) return false;
  for (const raw of domains) {
    const entry = (raw || '').trim();
    if (!entry) continue;
    const { host: eh, port: ep } = splitHostPort(entry);
    if (!eh) continue;
    if (eh === 'localhost') {
      if (host === 'localhost') {
        if (!ep) return true;       // entry "localhost" matches any port
        if (port === ep) return true; // entry "localhost:3000" matches exact port
      }
      continue;
    }
    if (eh.startsWith('*.')) {
      const suffix = eh.slice(2);
      if (host !== suffix && host.endsWith('.' + suffix)) {
        if (!ep || port === ep) return true;
      }
      continue;
    }
    if (host === eh) {
      if (!ep || port === ep) return true;
    }
  }
  return false;
}
